import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  DeleteForever as DeleteIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

interface CacheItem {
  key: string;
  label: string;
  description: string;
  size?: string;
}

const CacheClearPrompt: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [cleared, setCleared] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  
  const cacheItems: CacheItem[] = [
    {
      key: 'localStorage',
      label: 'Local Storage',
      description: 'User sessions, cart data, preferences',
      size: `${(JSON.stringify(localStorage).length / 1024).toFixed(2)} KB`
    },
    {
      key: 'sessionStorage',
      label: 'Session Storage',
      description: 'Temporary session data',
      size: `${(JSON.stringify(sessionStorage).length / 1024).toFixed(2)} KB`
    },
    {
      key: 'cookies',
      label: 'Cookies',
      description: 'Authentication and tracking cookies'
    },
    {
      key: 'serviceWorker',
      label: 'Service Worker Cache',
      description: 'Offline assets and cached data'
    }
  ];

  useEffect(() => {
    // Check for cache clear parameters
    const params = new URLSearchParams(location.search);
    const shouldClear = params.get('clear-cache');
    const autoConfirm = params.get('auto');
    
    if (shouldClear === 'true' || shouldClear === '1') {
      setOpen(true);
      setSelectedItems(cacheItems.map(item => item.key));
      
      if (autoConfirm === 'true') {
        handleClearCache();
      }
    }
    
    // Also check for debug mode
    const debugMode = params.get('debug');
    if (debugMode === 'true') {
      console.log('🔍 Debug Mode - Current Storage Status:');
      console.log('LocalStorage:', { ...localStorage });
      console.log('SessionStorage:', { ...sessionStorage });
      console.log('Cookies:', document.cookie);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleToggleItem = (key: string) => {
    setSelectedItems(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === cacheItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(cacheItems.map(item => item.key));
    }
  };

  const handleClearCache = async () => {
    setClearing(true);
    
    try {
      // Clear selected storage types
      if (selectedItems.includes('localStorage')) {
        localStorage.clear();
        console.log('✅ LocalStorage cleared');
      }
      
      if (selectedItems.includes('sessionStorage')) {
        sessionStorage.clear();
        console.log('✅ SessionStorage cleared');
      }
      
      if (selectedItems.includes('cookies')) {
        // Clear cookies (limited by browser security)
        document.cookie.split(";").forEach(cookie => {
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        });
        console.log('✅ Cookies cleared (accessible ones)');
      }
      
      if (selectedItems.includes('serviceWorker')) {
        // Clear service worker cache
        if ('serviceWorker' in navigator && 'caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames.map(cacheName => caches.delete(cacheName))
          );
          console.log('✅ Service Worker cache cleared');
          
          // Unregister service workers
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const registration of registrations) {
            await registration.unregister();
          }
          console.log('✅ Service Workers unregistered');
        }
      }
      
      setCleared(true);
      
      // Redirect after 2 seconds
      setTimeout(() => {
        // Remove cache clear params from URL
        const params = new URLSearchParams(location.search);
        params.delete('clear-cache');
        params.delete('auto');
        params.delete('debug');
        
        const newUrl = params.toString() 
          ? `${location.pathname}?${params.toString()}`
          : location.pathname;
          
        navigate(newUrl, { replace: true });
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      console.error('Error clearing cache:', error);
    } finally {
      setClearing(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Remove cache clear params from URL
    const params = new URLSearchParams(location.search);
    params.delete('clear-cache');
    params.delete('auto');
    
    const newUrl = params.toString() 
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;
      
    navigate(newUrl, { replace: true });
  };

  if (!open) return null;

  return (
    <Dialog 
      open={open} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        bgcolor: cleared ? 'success.light' : 'warning.light',
        color: cleared ? 'success.contrastText' : 'warning.contrastText'
      }}>
        {cleared ? (
          <>
            <CheckIcon />
            Cache Cleared Successfully
          </>
        ) : (
          <>
            <WarningIcon />
            Clear Application Cache
          </>
        )}
      </DialogTitle>
      
      <DialogContent>
        {cleared ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Selected cache items have been cleared!
            </Typography>
            <Typography color="text.secondary">
              The page will refresh in a moment...
            </Typography>
            <LinearProgress sx={{ mt: 3 }} />
          </Box>
        ) : (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Clearing cache will log you out and remove all stored data including:
              cart items, user sessions, and preferences.
            </Alert>
            
            <Typography variant="subtitle1" gutterBottom fontWeight="600">
              Select items to clear:
            </Typography>
            
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedItems.length === cacheItems.length}
                    indeterminate={selectedItems.length > 0 && selectedItems.length < cacheItems.length}
                    onChange={handleSelectAll}
                  />
                }
                label="Select All"
                sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}
              />
              
              {cacheItems.map((item) => (
                <Box key={item.key} sx={{ ml: 2, mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedItems.includes(item.key)}
                        onChange={() => handleToggleItem(item.key)}
                      />
                    }
                    label={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1">{item.label}</Typography>
                          {item.size && (
                            <Chip 
                              label={item.size} 
                              size="small" 
                              variant="outlined"
                              icon={<StorageIcon sx={{ fontSize: 16 }} />}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {item.description}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              ))}
            </FormGroup>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                <strong>Tip:</strong> Add <code>?clear-cache=true</code> to any URL to show this dialog.
                Add <code>&auto=true</code> to clear automatically.
              </Typography>
            </Alert>
          </>
        )}
      </DialogContent>
      
      {!cleared && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleClose} 
            disabled={clearing}
            sx={{
              color: theme.text,
              '&:hover': {
                backgroundColor: theme.background + '10'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleClearCache}
            variant="contained"
            startIcon={clearing ? null : <DeleteIcon />}
            disabled={clearing || selectedItems.length === 0}
            sx={{
              backgroundColor: 'var(--status-bad)',
              color: 'var(--text-inverse)',
              '&:hover': {
                backgroundColor: 'var(--status-bad)',
              },
              '&:disabled': {
                backgroundColor: theme.border || '#e5e7eb',
                color: theme.subtext || '#9ca3af'
              }
            }}
          >
            {clearing ? 'Clearing...' : `Clear ${selectedItems.length} Items`}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CacheClearPrompt;